import { useCallback, useEffect, useState } from "react";

import { adminApi, type InfrastructureResponse, type VaultStatusResponse } from "../api";
import { StatusTile } from "../components/StatusTile";

/** Re-read often enough that a stopped vault visibly ages on an open tab. */
const REFRESH_MS = 60_000;

function section(latest: Record<string, unknown> | null, key: string): Record<string, unknown> {
  const value = latest?.[key];
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function bytes(value: unknown): string {
  return typeof value === "number" ? `${(value / 1024 ** 4).toFixed(2)} TiB` : "—";
}

function when(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function Infrastructure(): JSX.Element {
  const [infra, setInfra] = useState<InfrastructureResponse | null>(null);
  const [vault, setVault] = useState<VaultStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    Promise.all([
      adminApi.get<InfrastructureResponse>("/api/v1/admin/infrastructure"),
      adminApi.get<VaultStatusResponse>("/api/v1/admin/vault/status"),
    ])
      .then(([i, v]) => {
        setInfra(i);
        setVault(v);
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const pool = section(vault?.latest ?? null, "pool");
  const array = section(vault?.latest ?? null, "array");
  const disks = Array.isArray(vault?.latest?.disks)
    ? (vault?.latest?.disks as Record<string, unknown>[])
    : [];

  return (
    <div>
      <h2 className="page-title">🖥️ Infrastructure</h2>
      <p className="page-subtitle">
        This process's own health, and the off-site vault as PiVault last reported it.
      </p>
      {error && <div className="error-banner">{error}</div>}

      {vault && (
        <div className="panel">
          <h3>Off-site vault{vault.host ? ` — ${vault.host}` : ""}</h3>
          <p className={`vault-age ${vault.status}`}>
            {vault.age_words
              ? `Last report ${vault.age_words} (expected every ${Math.round(
                  vault.interval_seconds / 60,
                )} min)`
              : "No report has ever been received."}
          </p>
          <div className="status-grid">
            {vault.checks.map((check) => (
              <StatusTile key={check.name} status={check} />
            ))}
          </div>
          {vault.last_received_at && (
            <>
              <p className="page-subtitle">
                {vault.reports_24h} of {vault.expected_24h} expected reports in the last 24 h. Gaps
                are the box being down or wedged, not a network blip that retried.
              </p>
              <table>
                <tbody>
                  <tr>
                    <th>Pool</th>
                    <td>
                      {pool.mounted === true ? "mounted" : "NOT mounted"}
                      {pool.fs_uuid_matches === false && " — wrong filesystem"} · free{" "}
                      {bytes(pool.free_b)} of {bytes(pool.total_b)}
                    </td>
                  </tr>
                  <tr>
                    <th>RAID array</th>
                    <td>
                      {String(array.state ?? "not reported")}
                      {typeof array.rebuild_pct === "number" &&
                        ` · rebuild ${array.rebuild_pct.toFixed(0)}%`}
                    </td>
                  </tr>
                  <tr>
                    <th>Disks</th>
                    <td>
                      {disks.length === 0
                        ? "not reported"
                        : disks
                            .map(
                              (d) =>
                                `${String(d.serial_masked ?? "?")}: pending ${String(
                                  d.pending_sectors ?? "?",
                                )}, reallocated ${String(d.reallocated ?? "?")}, self-test ${String(
                                  d.self_test ?? "?",
                                )}`,
                            )
                            .join(" · ")}
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
          {vault.boots_7d.length > 0 && (
            <>
              <h3>Boots seen in the last 7 days</h3>
              <p className="page-subtitle">
                More than one row here means the box restarted. On this hardware that has usually
                been a crash, not a shutdown.
              </p>
              <table>
                <thead>
                  <tr>
                    <th>Boot</th>
                    <th>First report</th>
                    <th>Last report</th>
                    <th>Reports</th>
                  </tr>
                </thead>
                <tbody>
                  {vault.boots_7d.map((b) => (
                    <tr key={b.boot_id + b.first_seen}>
                      <td>{b.boot_id.slice(0, 8) || "—"}</td>
                      <td>{when(b.first_seen)}</td>
                      <td>{when(b.last_seen)}</td>
                      <td>{b.reports}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          <p className="page-subtitle">{vault.durability_note}</p>
        </div>
      )}

      {infra && (
        <div className="panel">
          <h3>This process</h3>
          <div className="status-grid">
            <StatusTile status={infra.status} />
          </div>
          <table>
            <thead>
              <tr>
                <th>Counter</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(infra.counters).map(([name, value]) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td>{value.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
